import { IllegalOperationException } from '../exceptions/illegal-operation-exception.js';
import { isNotNullOrUndefined } from './null-safety.js';

export class VoidResult<E> {
  protected constructor(
    private readonly _isOk: boolean,
    private readonly _error: E | null,
  ) {}

  static ok<E>(_ = null): VoidResult<E> {
    return new VoidResult<E>(true, null);
  }

  static error<E>(error: E): VoidResult<E> {
    return new VoidResult<E>(false, error);
  }

  get isOk(): boolean {
    return this._isOk;
  }

  get isError(): boolean {
    return !this._isOk;
  }

  get error(): E {
    if(isNotNullOrUndefined(this._error)) {
      return this._error;
    }
    else {
      throw new IllegalOperationException('error is inaccessible on an ok instance');
    }
  }
}

type ResultValue<T> = T extends Result<infer U, unknown> ? U : never;
type ResultsError<T> = T extends Result<unknown, infer E>[] ? E : never;

export class Result<T, E> extends VoidResult<E> {
  private constructor(
      isOk: boolean,
      private readonly _value: T | null,
      error: E | null,
  ) {
    super(isOk, error);
  }

  static concat<T extends readonly Result<unknown, unknown>[] | []>(results: T): Result<{ -readonly [P in keyof T]: ResultValue<T[P]> }, ResultsError<T>> {
    const firstError = results.find((result) => result.isError);
    if(firstError) {
      return Result.error(firstError.error as ResultsError<T>);
    }

    return Result.ok(results.map((result) => result.value) as { -readonly [P in keyof T]: ResultValue<T[P]> });
  }

  static ok<T, E>(value: T): Result<T, E> {
    return new Result<T, E>(true, value, null);
  }

  static error<T, E>(error: E): Result<T, E> {
    return new Result<T, E>(false, null, error);
  }

  map<U>(mapper: (value: T) => U): Result<U, E> {
    if(this.isOk) {
      return Result.ok(mapper(this._value as T));
    }
    else {
      return Result.error(this.error);
    }
  }

  chain<U>(mapper: (value: T) => Result<U, E>): Result<U, E> {
    if(this.isOk) {
      return mapper(this._value as T);
    }
    else {
      return Result.error(this.error);
    }
  }

  get value(): T {
    if(this.isOk) {
      return this._value as T;
    }
    else {
      throw new IllegalOperationException('Result value is inaccessible on an error instance');
    }
  }

  unwrap(): T {
    if(this.isOk) {
      return this._value as T;
    }
    else {
      // Me no touchy black magic!
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw this.error;
    }
  }

  /**
   * @deprecated Use value instead.
   */
  get valueOrNull(): T | null {
    if(this.isOk) {
      return this._value;
    }
    else {
      throw new IllegalOperationException('Result value is inaccessible on an error instance');
    }
  }
}
